/**
 * Rules for measuring out the required dimensions for gutters around charts.
 * These gutters contain the axis and default padding around the charts.
 */

import { textGap, wrappedTextGap } from 'lib/text'
import { getBreakpoint } from '../breakpoints'

import {
  PADDING,
  AXIS_DIAGONAL_SKEW,
  LINE_HEIGHT,
  FONT_FAMILY,
  FONT_LARGE,
  FONT_SMALL,
  MIN_TOP,
  MIN_BOTTOM,
  MIN_RIGHT,
  MIN_LEFT,
  LABEL_PADDING,
  HORIZONTAL_TICK_HEIGHT
} from '../constants'

function getFont(breakpoint) {
  return `${breakpoint === 'large' ? FONT_LARGE : FONT_SMALL} ${FONT_FAMILY}`
}

export function getVerticalOffset({
  breakpoint,
  ticks = [],
  format,
  label,
  vertical,
  width
}) {
  let base = PADDING

  if (vertical) {
    // Increase the offset by the longest label
    base += textGap(ticks, format, getFont(breakpoint)) * AXIS_DIAGONAL_SKEW
  } else if (ticks.length) {
    base += HORIZONTAL_TICK_HEIGHT
  }

  if (breakpoint === 'large') {
    if (label) {
      base += labelDimensions(label, width).height
      // Add padding on each side of the label
      base += LABEL_PADDING * 2
    } else if (ticks.length) {
      // If there is no label, add the same amount of tick padding below the
      // tick's line-height
      base += PADDING
    }

    return base
  }

  return base
}

export function getHorizontalOffset({
  breakpoint,
  ticks = [],
  format,
  label,
  height
}) {
  // Padding on the top and bottom of ticks
  let base = PADDING

  // Factor in the longest tick string
  base += textGap(ticks, format, getFont(breakpoint))

  if (breakpoint === 'large' && label) {
    base += labelDimensions(label, height).height
    // Add padding on each side of the label
    base += LABEL_PADDING * 2
  } else {
    // If there is no label, add the same amount of tick padding to the
    base += PADDING
  }

  return base
}

export function labelDimensions(label, width) {
  return wrappedTextGap({
    font: getFont('large'),
    lineHeight: LINE_HEIGHT,
    text: label,
    width: width
  })
}

export function getGutters({
  breakpoint,
  top,
  left,
  bottom,
  right,
  width,
  height
}) {
  breakpoint = breakpoint || getBreakpoint(width)

  // First get the top and bottom gutters without any influence on dimentions
  let gutterTop = Math.max(
    MIN_TOP,
    getVerticalOffset({ breakpoint, width, height, ...top })
  )
  let gutterBottom = Math.max(
    MIN_BOTTOM,
    getVerticalOffset({ breakpoint, width, height, ...bottom })
  )

  // Now that we know that spacing, offset the height by those values
  height -= gutterTop + gutterBottom

  // Then get the left and right gutters
  let gutterLeft = Math.max(
    MIN_LEFT,
    getHorizontalOffset({ breakpoint, width, height, ...left })
  )
  let gutterRight = Math.max(
    MIN_RIGHT,
    getHorizontalOffset({ breakpoint, width, height, ...right })
  )

  // With that, reduce the width by that spacing
  width -= gutterLeft + gutterRight

  // Then recalculate the top and bottom gutter
  gutterTop = Math.max(
    MIN_TOP,
    getVerticalOffset({ breakpoint, width, height, ...top })
  )
  gutterBottom = Math.max(
    MIN_BOTTOM,
    getVerticalOffset({ breakpoint, width, height, ...bottom })
  )

  return {
    top: gutterTop,
    right: gutterRight,
    bottom: gutterBottom,
    left: gutterLeft,
    breakpoint: breakpoint
  }
}



// WEBPACK FOOTER //
// /home/runner/work/web-interactive/web-interactive/lib/components/charts/layouts/gutters.js